import { useTheme } from 'foundations-theming/utils';
import { BREAKPOINTS } from '../Grid/Grid.types';
import { rgba } from '../../utils/color';
import { useIconSize } from 'foundations-assets';
const gtSmallMediaQuery = `@media only screen and (min-width: ${BREAKPOINTS.sm}px)`;
const smallMediaQuery = `@media only screen and (max-width: ${BREAKPOINTS.sm}px)`;
import { TruncateStringContainer } from '../TruncateString/TruncateString';
import { useIsDisplayOptimized } from '../../context/DisplayOptimizedContext';
import { isFormUse } from './Button.helpers';
import { StyledButton, UnstyledButton } from './PrivateButton';
import { StyledButtonLink, UnstyledButtonLink } from './PrivateButtonLink';
import { createActiveStateSelector, createHoverStateSelector } from '../../utils/styles/styles';

// The type for background and border colors was broadened in
// `ButtonStateStyles` to `Color | RGBAColorToken | (string & {})` to accommodate
// setting a rgba and transparent, non-token values for the ghost and transparent
// button variants. In the future, this value may come from a token
// and we can remove the variables and type casts below.
const toColor = (theme, buttonStateColor) => {
  if (typeof buttonStateColor === 'object') {
    return rgba(theme.color[buttonStateColor.color], buttonStateColor.alpha);
  }
  return theme.color[buttonStateColor] || buttonStateColor;
};
export const useUseVariantCssObject = (isLinkUse, variantTokens) => {
  const theme = useTheme();
  return Object.assign({
    display: 'inline-block',
    overflow: isLinkUse ? 'visible' : 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: isLinkUse ? 'inherit' : 'nowrap',
    backgroundColor: toColor(theme, variantTokens.idle.backgroundColor),
    borderColor: toColor(theme, variantTokens.idle.borderColor),
    color: toColor(theme, variantTokens.idle.color),
    textDecoration: variantTokens.idle.textDecoration
  }, isLinkUse ? {
    border: 0,
    verticalAlign: 'inherit'
  } : {
    borderRadius: theme.borderRadius['100'],
    borderWidth: theme.borderWidth['100'],
    borderStyle: 'solid',
    verticalAlign: 'middle'
  }, {
    // hover styles should not override aria-invalid styles in an OPEN Select when anchorType='button'
    [createHoverStateSelector(':hover:not([aria-disabled="true"],[aria-pressed="true"],[aria-invalid="true"])')]: {
      backgroundColor: toColor(theme, variantTokens.hover.backgroundColor),
      borderColor: toColor(theme, variantTokens.hover.borderColor),
      color: toColor(theme, variantTokens.hover.color),
      textDecoration: variantTokens.hover.textDecoration,
      '& svg[data-icon-name]': {
        fill: toColor(theme, variantTokens.hover.color),
        transition: 'all 0.1s ease-in'
      }
    },
    [createActiveStateSelector(':active:not([aria-disabled="true"])')]: {
      backgroundColor: toColor(theme, variantTokens.active.backgroundColor),
      borderColor: toColor(theme, variantTokens.active.borderColor),
      color: toColor(theme, variantTokens.active.color),
      textDecoration: variantTokens.active.textDecoration,
      '& svg[data-icon-name]': {
        fill: toColor(theme, variantTokens.active.color)
      }
    },
    '&[aria-disabled="true"]': {
      backgroundColor: toColor(theme, variantTokens.disabled.backgroundColor),
      borderColor: toColor(theme, variantTokens.disabled.borderColor),
      color: toColor(theme, variantTokens.disabled.color),
      cursor: 'not-allowed',
      [`& ${TruncateStringContainer}`]: {
        cursor: 'not-allowed'
      },
      '& svg[data-icon-name]': {
        fill: toColor(theme, variantTokens.disabled.color)
      }
    }
  });
};
export const useSizeCssObject = sizeTokens => {
  var _theme$typography$siz;
  const theme = useTheme();
  const iconSize = useIconSize(sizeTokens.iconSize);
  return {
    paddingBlockStart: sizeTokens.paddingBlockStart && theme.space[sizeTokens.paddingBlockStart],
    paddingBlockEnd: sizeTokens.paddingBlockEnd && theme.space[sizeTokens.paddingBlockEnd],
    paddingInlineStart: theme.space[sizeTokens.paddingInlineStart],
    paddingInlineEnd: theme.space[sizeTokens.paddingInlineEnd],
    width: sizeTokens.width,
    maxWidth: '100%',
    /**
     * We want the all icons including external link icons to be same height.
     *
     * Because ExternalLinkIcons in Buttons already have a span around them,
     * we can set the height of ExternalLinkIcons to the same value as the width.
     * The width and height of ExternalLinkIcons are set in PrivateButtonLink.
     *
     * For all other icons, we have to set the height to the same height it would
     * be if it was also wrapped in a span.
     */
    '& svg[data-icon-name]': {
      width: iconSize,
      blockSize: ((_theme$typography$siz = theme.typography[sizeTokens.typography]) === null || _theme$typography$siz === void 0 ? void 0 : _theme$typography$siz.lineHeight) || iconSize
    },
    '& svg[data-icon-name="ExternalLink"]': {
      width: 'auto',
      blockSize: 'auto',
      verticalAlign: '-0.125em'
    }
  };
};
const getAutospacingStyles = (theme, responsive, use) => {
  let adjacentSiblingSelector = `& + ${StyledButton}, & + ${StyledButtonLink}`;
  if (use === 'unstyled') {
    adjacentSiblingSelector = `& + ${UnstyledButton}, & + ${UnstyledButtonLink}`;
  }
  return {
    [adjacentSiblingSelector]: responsive ? {
      [gtSmallMediaQuery]: {
        marginInlineStart: theme.space['300']
      },
      [smallMediaQuery]: {
        marginBlockStart: theme.space['200']
      }
    } : {
      marginInlineStart: theme.space['300']
    }
  };
};
export const useResponsiveStyles = ({
  responsive,
  isDropdown,
  use,
  isFilterBar
}) => {
  const theme = useTheme();
  return Object.assign({}, responsive && {
    [smallMediaQuery]: Object.assign({
      width: '100%'
    }, !isDropdown && {
      span: {
        justifyContent: 'center'
      }
    })
  }, !isFilterBar && getAutospacingStyles(theme, responsive, use));
};
export const useDisplayOptimizedCssStyles = use => {
  const theme = useTheme();
  const isDisplayOptimized = useIsDisplayOptimized();
  return isDisplayOptimized && isFormUse(use) ? {
    backgroundColor: 'transparent',
    border: 'none',
    paddingInlineStart: '0',
    fontSize: theme.typography['body-100'].fontSize,
    '&:hover:not([aria-disabled="true"]), &:focus:not([aria-disabled="true"]), &:focus-visible:not([aria-disabled="true"]), &:active:not([aria-disabled="true"]), &[aria-disabled="true"]': {
      backgroundColor: 'transparent'
    }
  } : {};
};